/**
 * Plotex live-sync — browser-side receiver for incremental chunk updates.
 *
 * Paired with the Blender plugin's ``plotex_live_sync.py`` + the Vite dev
 * plugin ``plotexLiveSyncPlugin()``, this module turns the artist's
 * "drag a vertex in Blender" into a ~1-second on-screen refresh, without
 * the full ``npm run pipeline`` + page-reload cycle.
 *
 * Dataflow
 * --------
 *   1. Artist edits a progressive-tagged mesh in Blender.
 *   2. plotex_live_sync.py re-exports ``public/chunks/mesh_<guid>.glb``.
 *   3. Plugin POSTs ``{type: "chunk_updated", guid, path, mtime}`` to
 *      the Vite dev server's ``/__plotex_live_notify`` endpoint.
 *   4. Vite plugin broadcasts that payload over the ``/__plotex_live``
 *      WebSocket to every connected browser.
 *   5. This class receives the message, calls
 *      ``lodManager.invalidateGuid(guid)``, and the LOD manager's picker
 *      re-fetches the now-fresh file on its next tick.
 *
 * Scope and failure modes
 * -----------------------
 * This class is a dev-server convenience. When the page is loaded from
 * file:// or from a production host that doesn't run the Vite plugin,
 * the WebSocket open will fail. Rather than spam the console with an
 * endless reconnect loop (which would worry users looking at prod logs),
 * we give up after ``MAX_STARTUP_FAILURES`` consecutive failures without
 * ever opening the socket cleanly. Once a connection succeeds at least
 * once, we keep retrying on drops forever — that's the "dev-server
 * restarted" case and reconnecting there is the whole point.
 *
 * Logging prefix: ``[plotex-live]``.
 */

import type { PlotexLodManager } from './PlotexLodManager';

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export interface PlotexLiveSyncOptions {
  /** Override the WebSocket URL. Defaults to ``ws://localhost:5173/__plotex_live``. */
  endpoint?: string;
}

/**
 * Shape of the JSON messages we expect on the wire. The plugin is a
 * trusted peer (localhost dev-server), so we don't defend against
 * hostile payloads — just guard against shape drift by checking for
 * the ``type`` discriminant.
 */
type LiveMessage =
  | {
      type: 'chunk_updated';
      guid: string;
      path?: string;
      mtime?: number;
    }
  | {
      type: 'hello';
      serverVersion?: string;
    };

/* -------------------------------------------------------------------------- */
/* Tunables                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Exponential reconnect backoff. We start at 1s and double up to the
 * 30s cap on each failed attempt. A single successful connection
 * resets the backoff, so a normal dev-server restart only pauses
 * us for ~1s once the server is back on the network.
 */
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

/**
 * If the socket never opens successfully AND we hit this many
 * back-to-back failures, give up. The user isn't running Vite (or
 * the plugin isn't installed in their dev-server) — no point
 * looping forever and polluting the console.
 */
const MAX_STARTUP_FAILURES = 3;

/* -------------------------------------------------------------------------- */
/* Class                                                                      */
/* -------------------------------------------------------------------------- */

export class PlotexLiveSync {
  private readonly lodManager: PlotexLodManager;
  private readonly endpoint: string;

  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private failureCount = 0;
  /**
   * Flips to true the moment we see a successful open. Lets us treat
   * "never connected" differently from "connected once, then dropped":
   * the former gives up after MAX_STARTUP_FAILURES, the latter keeps
   * trying forever so a dev-server restart is transparent.
   */
  private everOpened = false;
  private disposed = false;

  constructor(lodManager: PlotexLodManager, opts: PlotexLiveSyncOptions = {}) {
    this.lodManager = lodManager;
    this.endpoint = opts.endpoint ?? this.defaultEndpoint();
    this.connect();
  }

  /** Close the socket and stop reconnecting. Safe to call multiple times. */
  dispose(): void {
    this.disposed = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      try {
        // Strip handlers first so the close event doesn't trigger
        // another reconnect attempt during dispose.
        this.socket.onopen = null;
        this.socket.onclose = null;
        this.socket.onerror = null;
        this.socket.onmessage = null;
        if (
          this.socket.readyState === WebSocket.OPEN ||
          this.socket.readyState === WebSocket.CONNECTING
        ) {
          this.socket.close();
        }
      } catch {
        // Closing an already-closed socket can throw on some browsers —
        // not worth caring about during dispose.
      }
      this.socket = null;
    }
  }

  /* --- private --- */

  private defaultEndpoint(): string {
    // Running against the Vite dev server on the conventional port.
    // If the caller is embedding the engine elsewhere (different port,
    // reverse proxy, secure context), they pass an explicit endpoint
    // via options and this default never gets used.
    const host =
      typeof window !== 'undefined' && window.location?.hostname
        ? window.location.hostname
        : 'localhost';
    const port =
      typeof window !== 'undefined' && window.location?.port
        ? window.location.port
        : '5173';
    const protocol =
      typeof window !== 'undefined' && window.location?.protocol === 'https:'
        ? 'wss:'
        : 'ws:';
    return `${protocol}//${host}:${port}/__plotex_live`;
  }

  private connect(): void {
    if (this.disposed) return;
    // Defensive: if WebSocket isn't available in this context (SSR, old
    // browser, file:// restriction), silently bail.
    if (typeof WebSocket === 'undefined') {
      return;
    }

    let ws: WebSocket;
    try {
      ws = new WebSocket(this.endpoint);
    } catch (err) {
      // Constructor itself threw — unusual but happens with bad URLs
      // (e.g., blocked in the Chrome security origin). Treat as a
      // startup failure and back off.
      this.handleConnectionFailure(err);
      return;
    }
    this.socket = ws;

    ws.onopen = () => {
      this.everOpened = true;
      this.failureCount = 0;
      console.log(`[plotex-live] connected to ${this.endpoint}`);
    };

    ws.onmessage = (ev) => {
      this.handleMessage(ev.data);
    };

    ws.onerror = () => {
      // Browsers deliberately hide WebSocket error details to avoid
      // network-probe leakage, so there's nothing useful to log here.
      // The subsequent onclose handler does the actual failure
      // accounting. We keep this as a no-op to prevent the default
      // Error event from bubbling as an uncaught exception.
    };

    ws.onclose = (ev) => {
      this.socket = null;
      if (this.disposed) return;
      this.handleClose(ev);
    };
  }

  private handleConnectionFailure(err: unknown): void {
    this.failureCount++;
    if (!this.everOpened && this.failureCount >= MAX_STARTUP_FAILURES) {
      console.log(
        `[plotex-live] disabling live sync — ` +
          `could not connect to ${this.endpoint} after ` +
          `${MAX_STARTUP_FAILURES} attempts (Vite plugin not installed?)`
      );
      this.disposed = true;
      return;
    }
    console.log(
      `[plotex-live] connection failed (${String(err)}), retrying in ` +
        `${this.computeBackoff()}ms`
    );
    this.scheduleReconnect();
  }

  private handleClose(ev: CloseEvent): void {
    if (!this.everOpened) {
      // Plain startup failure path — same accounting as a constructor
      // throw.
      this.handleConnectionFailure(`close code ${ev.code}`);
      return;
    }
    // We had a live connection and it dropped. Keep retrying forever —
    // most common cause is the dev-server restarting and the artist
    // will want reconnect to Just Work when it comes back.
    console.log(
      `[plotex-live] socket closed (code=${ev.code}); reconnecting in ` +
        `${this.computeBackoff()}ms`
    );
    this.failureCount++;
    this.scheduleReconnect();
  }

  private computeBackoff(): number {
    // failureCount is incremented BEFORE this is called, so attempt #1
    // uses RECONNECT_BASE_MS, attempt #2 uses 2x, #3 uses 4x, etc.
    const step = Math.max(1, this.failureCount);
    const ms = RECONNECT_BASE_MS * Math.pow(2, step - 1);
    return Math.min(ms, RECONNECT_MAX_MS);
  }

  private scheduleReconnect(): void {
    if (this.disposed) return;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
    }
    const delay = this.computeBackoff();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private handleMessage(raw: unknown): void {
    // We only speak JSON — binary frames are not part of the protocol.
    if (typeof raw !== 'string') return;
    let msg: LiveMessage | null = null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.type === 'string') {
        msg = parsed as LiveMessage;
      }
    } catch {
      console.warn(`[plotex-live] dropping malformed payload`);
      return;
    }
    if (!msg) return;

    if (msg.type === 'chunk_updated') {
      const guid = msg.guid;
      if (typeof guid !== 'string' || guid.length === 0) {
        console.warn('[plotex-live] chunk_updated missing guid');
        return;
      }
      try {
        this.lodManager.invalidateGuid(guid);
        console.log(
          `[plotex-live] invalidated cache for ${guid.slice(0, 8)} ` +
            `(path=${msg.path ?? 'n/a'}, mtime=${msg.mtime ?? 0})`
        );
      } catch (err) {
        console.error('[plotex-live] invalidateGuid threw:', err);
      }
      return;
    }

    if (msg.type === 'hello') {
      // Advisory payload the Vite plugin sends on connect. Purely
      // informational for now; logged so the artist can see the
      // handshake in DevTools when debugging.
      console.log(
        `[plotex-live] server hello (version=${msg.serverVersion ?? 'n/a'})`
      );
      return;
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Helper — factory attachment                                                */
/* -------------------------------------------------------------------------- */

/**
 * Attach a ``PlotexLiveSync`` to an engine instance when we're running
 * on localhost (i.e., against the Vite dev server). Returns null off-
 * host so the production build stays completely free of reconnect
 * timers + idle WebSocket frames.
 *
 * The "on localhost" check uses ``window.location.hostname`` — that's
 * cheap, works in every browser, and correctly distinguishes dev from
 * ``plotex.yourdomain.com`` production deployments. IP-literal
 * ``127.0.0.1`` is accepted too for people who type it that way.
 *
 * Callers: the main engine factory in ``src/index.ts`` invokes this
 * after the LodManager is constructed and stores the handle on a
 * private field so ``engine.dispose()`` can tear the socket down.
 */
export function attachLiveSync(
  engine: { lodManager: PlotexLodManager },
  endpoint?: string
): PlotexLiveSync | null {
  if (typeof window === 'undefined') return null;
  const host = window.location?.hostname ?? '';
  const isLocal =
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '' || // file:// case — still allow, user might want it
    host === '::1';
  if (!isLocal) return null;

  try {
    return new PlotexLiveSync(engine.lodManager, endpoint ? { endpoint } : {});
  } catch (err) {
    console.warn('[plotex-live] attachLiveSync failed:', err);
    return null;
  }
}
